import { API_CONFIG } from '../config/api';

import { getAccessToken } from './accessTokenManager';

// HTTP Status Codes
const HTTP_STATUS_NO_CONTENT = 204;

// HTTP Methods
const HTTP_METHOD_POST = 'POST';

// HTTP Headers
const CONTENT_TYPE_HEADER = 'content-type';
const AUTHORIZATION_HEADER = 'Authorization';
const CONTENT_TYPE_JSON = 'application/json';

// Fetch error names / messages
const ABORT_ERROR_NAME = 'AbortError';
const ERROR_REQUEST_TIMEOUT = 'Request timeout';
const ERROR_NETWORK = 'Network error occurred';

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: Response,
    public correlationId?: string,
  ) {
    // Build error message starting with the base message
    let errorMessage = message;

    // Add status code if provided and not already in the message
    if (status !== null && status !== undefined && !message.includes('Status:')) {
      errorMessage += ` (Status: ${status})`;
    }

    // Add response status text if response is provided
    if (response !== null && response !== undefined && !message.includes('Response:')) {
      const statusText = response.statusText ?? 'Unknown';
      errorMessage += ` (Response: ${statusText})`;
    }

    // Add correlation ID if provided
    if (correlationId && !message.includes('Correlation ID:')) {
      errorMessage += ` (Correlation ID: ${correlationId})`;
    }

    super(errorMessage);
    this.name = 'ApiError';
  }
}

export class ApiClient {
  private baseUrl: string;
  private timeout: number;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
  }

  /**
   * Generic HTTP request method
   * @param endpoint - API endpoint
   * @param options - Fetch options
   * @return Promise with response data
   * @throws ApiError on HTTP errors
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    // Only set Content-Type for requests that have a body
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };
    
    // Add authentication token if available
    const token = getAccessToken();
    if (token) {
      headers[AUTHORIZATION_HEADER] = `Bearer ${token}`;
    }
    
    // Add Content-Type only if there's a body to send and it's not FormData
    if (options.body && !(options.body instanceof FormData)) {
      headers['Content-Type'] = CONTENT_TYPE_JSON;
    }

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw await this.handleErrorResponse(response);
      }

      // Handle 204 No Content responses (like DELETE operations)
      if (response.status === 204) {
        return undefined as T;
      }

      // Check if the response has content to parse
      return await this.parseResponseBody<T>(response);
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof Error && error.name === ABORT_ERROR_NAME) {
        throw new ApiError(ERROR_REQUEST_TIMEOUT);
      }

      throw new ApiError(ERROR_NETWORK);
    }
  }

  /**
   * Generic HTTP request method with custom timeout
   * @param endpoint - API endpoint
   * @param options - Fetch options
   * @param timeoutMs - Custom timeout in milliseconds
   * @return Promise with response data
   * @throws ApiError on HTTP errors
   */
  private async requestWithTimeout<T>(
    endpoint: string,
    options: RequestInit = {},
    timeoutMs?: number,
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const effectiveTimeout = timeoutMs ?? this.timeout;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), effectiveTimeout);

    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    const token = getAccessToken();
    if (token) {
      headers[AUTHORIZATION_HEADER] = `Bearer ${token}`;
    }

    if (options.body && !(options.body instanceof FormData)) {
      headers['Content-Type'] = CONTENT_TYPE_JSON;
    }

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw await this.handleErrorResponse(response);
      }

      if (response.status === HTTP_STATUS_NO_CONTENT) {
        return undefined as T;
      }

      const contentLength = response.headers.get('content-length');
      const contentType = response.headers.get(CONTENT_TYPE_HEADER);

      if (contentLength === '0' || !contentType?.includes(CONTENT_TYPE_JSON)) {
        return undefined as T;
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof Error && error.name === ABORT_ERROR_NAME) {
        throw new ApiError(ERROR_REQUEST_TIMEOUT);
      }

      throw new ApiError(ERROR_NETWORK);
    }
  }

  /**
   * Handle error responses
   */
  private async handleErrorResponse(response: Response): Promise<ApiError> {
    // Extract correlation ID from response headers
    const correlationId = response.headers.get('x-correlation-id') ??
      response.headers.get('correlation-id') ??
      undefined;

    // Try to extract error detail from response body
    let errorDetail = `HTTP error! status: ${response.status}`;
    try {
      const contentType = response.headers.get(CONTENT_TYPE_HEADER);
      if (contentType?.includes(CONTENT_TYPE_JSON)) {
        const errorData = await response.json();
        if (errorData.detail) {
          errorDetail = errorData.detail;
        }
      }
    } catch {
      // If parsing fails, use default error message
    }

    return new ApiError(
      errorDetail,
      response.status,
      response,
      correlationId,
    );
  }

  /**
   * Parse response body
   */
  private async parseResponseBody<T>(response: Response): Promise<T> {
    // Check if the response has content to parse
    const contentLength = response.headers.get('content-length');
    const contentType = response.headers.get(CONTENT_TYPE_HEADER);

    if (contentLength === '0' || !contentType?.includes(CONTENT_TYPE_JSON)) {
      return undefined as T;
    }

    return response.json();
  }

  /**
   * GET request
   * @param endpoint - API endpoint
   * @return Promise with response data
   */
  public async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  /**
   * POST request
   * @param endpoint - API endpoint
   * @param data - Request body data
   * @return Promise with response data
   */
  public async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: HTTP_METHOD_POST,
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * POST request with custom timeout for long-running operations
   * @param endpoint - API endpoint
   * @param data - Request body data
   * @param timeoutMs - Custom timeout in milliseconds
   * @return Promise with response data
   */
  public async postWithTimeout<T>(
    endpoint: string,
    data?: unknown,
    timeoutMs?: number,
  ): Promise<T> {
    return this.requestWithTimeout<T>(
      endpoint,
      {
        method: HTTP_METHOD_POST,
        body: data ? JSON.stringify(data) : undefined,
      },
      timeoutMs,
    );
  }

  /**
   * PUT request
   * @param endpoint - API endpoint
   * @param data - Request body data
   * @return Promise with response data
   */
  public async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PATCH request
   * @param endpoint - API endpoint
   * @param data - Request body data
   * @return Promise with response data
   */
  public async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE request
   * @param endpoint - API endpoint
   * @return Promise with response data
   */
  public async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * GET request returning a binary Blob with custom timeout
   * @param endpoint - API endpoint
   * @param timeoutMs - Custom timeout in milliseconds
   * @return Promise with Blob data
   * @throws ApiError on HTTP errors
   */
  public async getBlob(
    endpoint: string,
    timeoutMs?: number,
  ): Promise<Blob> {
    const url = `${this.baseUrl}${endpoint}`;
    const effectiveTimeout = timeoutMs ?? this.timeout;

    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      effectiveTimeout,
    );

    const headers: Record<string, string> = {};

    const token = getAccessToken();
    if (token) {
      headers[AUTHORIZATION_HEADER] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        signal: controller.signal,
        headers,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw await this.handleErrorResponse(response);
      }

      return await response.blob();
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof ApiError) {
        throw error;
      }

      if (
        error instanceof Error
        && error.name === ABORT_ERROR_NAME
      ) {
        throw new ApiError(ERROR_REQUEST_TIMEOUT);
      }

      throw new ApiError(ERROR_NETWORK);
    }
  }

  /**
   * Upload file using FormData
   * @param endpoint - API endpoint
   * @param file - File to upload
   * @param additionalFields - Additional form fields
   * @return Promise with response data
   */
  public async uploadFile<T>(
    endpoint: string,
    file: File,
    additionalFields?: Record<string, string>,
  ): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (additionalFields) {
      Object.entries(additionalFields).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    return this.requestWithTimeout<T>(
      endpoint,
      {
        method: HTTP_METHOD_POST,
        body: formData,
        // Don't set Content-Type header for FormData - browser will set it with boundary
      },
      API_CONFIG.LONG_RUNNING_TIMEOUT,
    );
  }

  /**
   * POST request with FormData
   * @param endpoint - API endpoint
   * @param formData - FormData to upload
   * @return Promise with response data
   */
  public async postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
    return this.requestWithTimeout<T>(
      endpoint,
      {
        method: HTTP_METHOD_POST,
        body: formData,
        // Don't set Content-Type header for FormData - browser will set it with boundary
      },
      API_CONFIG.LONG_RUNNING_TIMEOUT,
    );
  }
}

export const apiClient = new ApiClient();
